import React, { useEffect, useMemo, useState } from 'react';
import { DataTable, SectionFeedback, SummaryCard, TableControls, filterAndSortRows, formatDateTimeLocal } from './CommercialCommon';

function toUtcIsoFromLocalInput(value) {
  return value ? new Date(value).toISOString() : '';
}

async function runPatch({ api, endpoint, userEmail, payload, setFeedback, setSaving, successMessage, onSuccess }) {
  setFeedback({ error: '', success: '' });
  setSaving(true);
  try {
    await api.patch(endpoint, { requesterEmail: userEmail, ...payload });
    setFeedback({ error: '', success: successMessage || 'Changes saved.' });
    onSuccess?.();
  } catch (err) {
    setFeedback({ error: err.response?.data?.message || 'Unable to save your changes.', success: '' });
  } finally {
    setSaving(false);
  }
}

async function runPost({ api, endpoint, userEmail, payload, setFeedback, setSaving, successMessage, onSuccess }) {
  setFeedback({ error: '', success: '' });
  setSaving(true);
  try {
    await api.post(endpoint, { requesterEmail: userEmail, ...payload });
    setFeedback({ error: '', success: successMessage || 'Saved.' });
    onSuccess?.();
  } catch (err) {
    setFeedback({ error: err.response?.data?.message || 'Unable to save your changes.', success: '' });
  } finally {
    setSaving(false);
  }
}

function buildSortOptions(options) {
  return options.map(option => ({ value: option.value, label: option.label }));
}

export function CommercialWorkOrdersTab({ api, user, workspace, capabilities, onRefresh }) {
  const canManage = Boolean(capabilities?.canManageWorkOrders);
  const canViewAudit = Boolean(capabilities?.canViewAudit);
  const [feedback, setFeedback] = useState({ error: '', success: '' });
  const [saving, setSaving] = useState('');
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('workOrderNumber');
  const [sortDirection, setSortDirection] = useState('desc');
  const [filterValues, setFilterValues] = useState({ status: '', priority: '' });
  const [selectedId, setSelectedId] = useState('');
  const [auditLogs, setAuditLogs] = useState([]);
  const [form, setForm] = useState({ title: '', description: '', status: 'unassigned', priority: 'medium', customerId: '', assetId: '', teamId: '', assignedTechnicianId: '', scheduledStartUtc: '', scheduledEndUtc: '' });

  const rows = useMemo(() => filterAndSortRows(workspace.workOrders, {
    search,
    searchKeys: ['workOrderNumber', 'title', 'status', 'priority', 'customerName', 'assetName', 'teamName', 'assignedTechnicianName'],
    sortKey,
    sortDirection,
    filters: filterValues,
    filterAccessors: { status: row => row.status || '', priority: row => row.priority || '' },
  }), [workspace.workOrders, search, sortKey, sortDirection, filterValues]);

  const selectedRow = useMemo(() => rows.find(row => row.id === selectedId) || rows[0] || null, [rows, selectedId]);
  const [draft, setDraft] = useState(null);

  useEffect(() => {
    if (selectedRow) {
      setSelectedId(selectedRow.id);
      setDraft({
        title: selectedRow.title || '',
        description: selectedRow.description || '',
        status: selectedRow.status || 'unassigned',
        priority: selectedRow.priority || 'medium',
        customerId: selectedRow.customerId || '',
        assetId: selectedRow.assetId || '',
        teamId: selectedRow.teamId || '',
        assignedTechnicianId: selectedRow.assignedTechnicianId || '',
        scheduledStartUtc: selectedRow.scheduledStartUtc ? selectedRow.scheduledStartUtc.slice(0, 16) : '',
        scheduledEndUtc: selectedRow.scheduledEndUtc ? selectedRow.scheduledEndUtc.slice(0, 16) : '',
        actualStartUtc: selectedRow.actualStartUtc ? selectedRow.actualStartUtc.slice(0, 16) : '',
        actualCompleteUtc: selectedRow.actualCompleteUtc ? selectedRow.actualCompleteUtc.slice(0, 16) : '',
        resolutionNotes: selectedRow.resolutionNotes || '',
      });
    } else {
      setDraft(null);
    }
  }, [selectedRow]);

  useEffect(() => {
    let isMounted = true;
    const loadAudit = async () => {
      if (!api || !user?.email || !selectedId || !canViewAudit) {
        if (isMounted) setAuditLogs([]);
        return;
      }
      try {
        const res = await api.get('/commercial/audit-logs', { params: { requesterEmail: user.email, entityType: 'work_order', entityId: selectedId, limit: 20 } });
        if (isMounted) setAuditLogs(res.data.auditLogs || []);
      } catch {
        if (isMounted) setAuditLogs([]);
      }
    };
    loadAudit();
    return () => { isMounted = false; };
  }, [api, user, selectedId, canViewAudit]);

  return (
    <>
      <section className="commercial-panel">
        <p className="commercial-panel__eyebrow">Work Orders</p>
        <h2 className="commercial-panel__title">Work Order Queue</h2>
        <SectionFeedback error={feedback.error} success={feedback.success} />
      </section>

      {canManage ? (
        <section className="commercial-panel">
          <h3 className="commercial-section-title">Create Work Order</h3>
          <div className="commercial-form commercial-form__grid">
            <label className="commercial-form__field"><span>Title</span><input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} /></label>
            <label className="commercial-form__field"><span>Status</span><select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>{workspace.workOrderStatuses.map(status => <option key={status} value={status}>{status}</option>)}</select></label>
            <label className="commercial-form__field"><span>Priority</span><select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}>{workspace.workOrderPriorities.map(priority => <option key={priority} value={priority}>{priority}</option>)}</select></label>
            <label className="commercial-form__field"><span>Customer</span><select value={form.customerId} onChange={e => setForm({ ...form, customerId: e.target.value })}><option value="">Select a customer</option>{workspace.customers.map(customer => <option key={customer.id} value={customer.id}>{customer.name}</option>)}</select></label>
            <label className="commercial-form__field"><span>Asset</span><select value={form.assetId} onChange={e => setForm({ ...form, assetId: e.target.value })}><option value="">Select an asset</option>{workspace.assets.map(asset => <option key={asset.id} value={asset.id}>{asset.name}</option>)}</select></label>
            <label className="commercial-form__field"><span>Team</span><select value={form.teamId} onChange={e => setForm({ ...form, teamId: e.target.value })}><option value="">Select a team</option>{workspace.teams.map(team => <option key={team.id} value={team.id}>{team.name}</option>)}</select></label>
            <label className="commercial-form__field"><span>Technician</span><select value={form.assignedTechnicianId} onChange={e => setForm({ ...form, assignedTechnicianId: e.target.value })}><option value="">Select a technician</option>{workspace.technicians.map(technician => <option key={technician.id} value={technician.id}>{technician.displayName}</option>)}</select></label>
            <label className="commercial-form__field"><span>Scheduled Start</span><input type="datetime-local" value={form.scheduledStartUtc} onChange={e => setForm({ ...form, scheduledStartUtc: e.target.value })} /></label>
            <label className="commercial-form__field"><span>Scheduled End</span><input type="datetime-local" value={form.scheduledEndUtc} onChange={e => setForm({ ...form, scheduledEndUtc: e.target.value })} /></label>
            <label className="commercial-form__field commercial-form__field--full"><span>Description</span><textarea rows={3} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></label>
            <div className="commercial-form__actions"><button type="button" disabled={saving === 'create'} onClick={() => { setSaving('create'); runPost({ api, endpoint: '/commercial/work-orders', userEmail: user.email, payload: { ...form, scheduledStartUtc: toUtcIsoFromLocalInput(form.scheduledStartUtc), scheduledEndUtc: toUtcIsoFromLocalInput(form.scheduledEndUtc) }, setFeedback, setSaving: value => setSaving(value ? 'create' : ''), successMessage: 'Work order created.', onSuccess: () => { setForm({ title: '', description: '', status: 'unassigned', priority: 'medium', customerId: '', assetId: '', teamId: '', assignedTechnicianId: '', scheduledStartUtc: '', scheduledEndUtc: '' }); onRefresh?.(); } }); }}>{saving === 'create' ? 'Saving...' : 'Create Work Order'}</button></div>
          </div>
        </section>
      ) : null}

      <section className="commercial-grid commercial-grid--equal">
        <section className="commercial-panel">
          <h3 className="commercial-section-title">Work Orders</h3>
          <TableControls search={search} onSearchChange={setSearch} searchPlaceholder="Search work orders" sortKey={sortKey} onSortKeyChange={setSortKey} sortDirection={sortDirection} onSortDirectionChange={setSortDirection} sortOptions={buildSortOptions([{ value: 'workOrderNumber', label: 'Work Order' }, { value: 'title', label: 'Title' }, { value: 'status', label: 'Status' }, { value: 'priority', label: 'Priority' }])} filters={[{ key: 'status', label: 'Status', options: workspace.workOrderStatuses.map(status => ({ value: status, label: status })) }, { key: 'priority', label: 'Priority', options: workspace.workOrderPriorities.map(priority => ({ value: priority, label: priority })) }]} filterValues={filterValues} onFilterChange={(key, value) => setFilterValues(current => ({ ...current, [key]: value }))} onClear={() => { setSearch(''); setSortKey('workOrderNumber'); setSortDirection('desc'); setFilterValues({ status: '', priority: '' }); }} />
          <DataTable columns={[{ key: 'workOrderNumber', label: 'Work Order' }, { key: 'title', label: 'Title' }, { key: 'status', label: 'Status' }, { key: 'priority', label: 'Priority' }, { key: 'customerName', label: 'Customer' }, { key: 'assignedTechnicianName', label: 'Assigned To', render: row => row.assignedTechnicianName || 'Unassigned' }, { key: 'scheduledStartUtc', label: 'Scheduled Start', render: row => row.scheduledStartUtc ? formatDateTimeLocal(row.scheduledStartUtc) : 'Not scheduled' }]} rows={rows} emptyMessage="No work orders yet." onRowSelect={row => setSelectedId(row.id)} selectedRowId={selectedRow?.id} />
        </section>
        <section className="commercial-panel commercial-panel--subtle">
          <h3 className="commercial-section-title">Work Order Details</h3>
          {draft ? (
            <div className="commercial-form">
              <label className="commercial-form__field"><span>Title</span><input value={draft.title} onChange={e => setDraft({ ...draft, title: e.target.value })} /></label>
              <label className="commercial-form__field"><span>Status</span><select value={draft.status} onChange={e => setDraft({ ...draft, status: e.target.value })}>{workspace.workOrderStatuses.map(status => <option key={status} value={status}>{status}</option>)}</select></label>
              <label className="commercial-form__field"><span>Priority</span><select value={draft.priority} onChange={e => setDraft({ ...draft, priority: e.target.value })}>{workspace.workOrderPriorities.map(priority => <option key={priority} value={priority}>{priority}</option>)}</select></label>
              <label className="commercial-form__field"><span>Customer</span><select value={draft.customerId || ''} onChange={e => setDraft({ ...draft, customerId: e.target.value })}><option value="">Select a customer</option>{workspace.customers.map(customer => <option key={customer.id} value={customer.id}>{customer.name}</option>)}</select></label>
              <label className="commercial-form__field"><span>Asset</span><select value={draft.assetId || ''} onChange={e => setDraft({ ...draft, assetId: e.target.value })}><option value="">Select an asset</option>{workspace.assets.map(asset => <option key={asset.id} value={asset.id}>{asset.name}</option>)}</select></label>
              <label className="commercial-form__field"><span>Team</span><select value={draft.teamId || ''} onChange={e => setDraft({ ...draft, teamId: e.target.value })}><option value="">Select a team</option>{workspace.teams.map(team => <option key={team.id} value={team.id}>{team.name}</option>)}</select></label>
              <label className="commercial-form__field"><span>Technician</span><select value={draft.assignedTechnicianId || ''} onChange={e => setDraft({ ...draft, assignedTechnicianId: e.target.value })}><option value="">Select a technician</option>{workspace.technicians.map(technician => <option key={technician.id} value={technician.id}>{technician.displayName}</option>)}</select></label>
              <label className="commercial-form__field"><span>Scheduled Start</span><input type="datetime-local" value={draft.scheduledStartUtc || ''} onChange={e => setDraft({ ...draft, scheduledStartUtc: e.target.value })} /></label>
              <label className="commercial-form__field"><span>Scheduled End</span><input type="datetime-local" value={draft.scheduledEndUtc || ''} onChange={e => setDraft({ ...draft, scheduledEndUtc: e.target.value })} /></label>
              <label className="commercial-form__field"><span>Actual Start</span><input type="datetime-local" value={draft.actualStartUtc || ''} onChange={e => setDraft({ ...draft, actualStartUtc: e.target.value })} /></label>
              <label className="commercial-form__field"><span>Actual Complete</span><input type="datetime-local" value={draft.actualCompleteUtc || ''} onChange={e => setDraft({ ...draft, actualCompleteUtc: e.target.value })} /></label>
              <label className="commercial-form__field commercial-form__field--full"><span>Description</span><textarea rows={3} value={draft.description || ''} onChange={e => setDraft({ ...draft, description: e.target.value })} /></label>
              <label className="commercial-form__field commercial-form__field--full"><span>Resolution Notes</span><textarea rows={3} value={draft.resolutionNotes || ''} onChange={e => setDraft({ ...draft, resolutionNotes: e.target.value })} /></label>
              {canManage ? (
                <div className="commercial-detail-actions">
                  <button type="button" disabled={saving === 'update'} onClick={() => runPatch({ api, endpoint: `/commercial/work-orders/${selectedRow.id}`, userEmail: user.email, payload: { ...draft, scheduledStartUtc: toUtcIsoFromLocalInput(draft.scheduledStartUtc), scheduledEndUtc: toUtcIsoFromLocalInput(draft.scheduledEndUtc), actualStartUtc: toUtcIsoFromLocalInput(draft.actualStartUtc), actualCompleteUtc: toUtcIsoFromLocalInput(draft.actualCompleteUtc) }, setFeedback, setSaving: value => setSaving(value ? 'update' : ''), successMessage: 'Work order updated.', onSuccess: onRefresh })}>{saving === 'update' ? 'Saving...' : 'Save Work Order'}</button>
                  <button type="button" className="commercial-table-controls__ghost" disabled={saving === 'archive'} onClick={() => runPost({ api, endpoint: `/commercial/work-orders/${selectedRow.id}/archive`, userEmail: user.email, payload: { archived: true }, setFeedback, setSaving: value => setSaving(value ? 'archive' : ''), successMessage: 'Work order archived.', onSuccess: () => { setSelectedId(''); onRefresh?.(); } })}>Archive</button>
                </div>
              ) : (
                <p className="commercial-form__hint">You can review this work order, but only users with work-order access can change it.</p>
              )}
            </div>
          ) : <p className="commercial-form__hint">Select a work order to review its details.</p>}
        </section>
      </section>

      {canViewAudit ? (
        <section className="commercial-panel">
          <h3 className="commercial-section-title">Activity History</h3>
          <DataTable columns={[{ key: 'createdAt', label: 'Time', render: row => formatDateTimeLocal(row.createdAt) }, { key: 'summary', label: 'Summary' }, { key: 'changedByEmail', label: 'Changed By', render: row => row.changedByEmail || 'System' }]} rows={auditLogs} emptyMessage="No activity history yet." />
        </section>
      ) : null}
    </>
  );
}

export function CommercialDispatchTab({ api, user, workspace, capabilities, onRefresh }) {
  const canManage = Boolean(capabilities?.canManageDispatch);
  const [error, setError] = useState('');

  const handleStatusChange = async (workOrderId, status) => {
    setError('');
    try {
      await api.post(`/commercial/work-orders/${workOrderId}/status`, { requesterEmail: user.email, status });
      onRefresh?.();
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to update work order status.');
    }
  };

  return (
    <>
      <section className="commercial-panel">
        <p className="commercial-panel__eyebrow">Dispatch</p>
        <h2 className="commercial-panel__title">Dispatch Board</h2>
        <SectionFeedback error={error} success="" />
      </section>
      <div className="commercial-board">
        {workspace.workOrderStatuses.filter(status => status !== 'closed').map(status => {
          const cards = workspace.workOrders.filter(workOrder => workOrder.status === status);
          return (
            <section key={status} className="commercial-board__column">
              <h3>{status.replaceAll('_', ' ')}</h3>
              <div className="commercial-board__stack">
                {cards.length > 0 ? cards.map(card => (
                  <article key={card.id} className="commercial-board__card">
                    <p className="commercial-board__card-title">{card.workOrderNumber}</p>
                    <p className="commercial-board__card-copy">{card.title}</p>
                    <p className="commercial-form__hint">{card.assignedTechnicianName || card.teamName || 'Unassigned'}</p>
                    {canManage ? (
                      <select value={card.status} onChange={e => handleStatusChange(card.id, e.target.value)}>
                        {workspace.workOrderStatuses.map(option => <option key={option} value={option}>{option}</option>)}
                      </select>
                    ) : (
                      <p className="commercial-form__hint">{card.status}</p>
                    )}
                  </article>
                )) : <div className="commercial-board__empty">No work orders in this stage</div>}
              </div>
            </section>
          );
        })}
      </div>
    </>
  );
}

export function CommercialReportsTab({ api, user, workspace }) {
  const [reports, setReports] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let isMounted = true;
    const loadReports = async () => {
      if (!api || !user?.email || !workspace.tenant) return;
      try {
        const res = await api.get('/commercial/reports/summary', { params: { requesterEmail: user.email } });
        if (isMounted) setReports(res.data);
      } catch (err) {
        if (isMounted) setError(err.response?.data?.message || 'Unable to load reports summary.');
      }
    };
    loadReports();
    return () => { isMounted = false; };
  }, [api, user, workspace.tenant]);

  const summary = reports?.summary || workspace.summary;

  return (
    <>
      <section className="commercial-panel">
        <p className="commercial-panel__eyebrow">Reports</p>
        <h2 className="commercial-panel__title">Reports Overview</h2>
        <SectionFeedback error={error} success="" />
      </section>
      <section className="commercial-summary-grid">
        <SummaryCard label="Total Work Orders" value={summary.workOrderCount} />
        <SummaryCard label="Completed / Closed" value={reports?.completedWorkOrders ?? 0} tone="success" />
        <SummaryCard label="Open" value={summary.openWorkOrderCount} tone="warning" />
        <SummaryCard label="Dispatch Queue" value={summary.dispatchQueueCount} tone="accent" />
      </section>
      <section className="commercial-grid commercial-grid--equal">
        <section className="commercial-panel">
          <h3 className="commercial-section-title">Work Orders by Status</h3>
          <div className="commercial-stat-list">
            {Object.entries(reports?.workOrdersByStatus || summary.statusBreakdown || {}).map(([status, count]) => (
              <div key={status} className="commercial-stat-list__row"><span>{status.replaceAll('_', ' ')}</span><strong>{count}</strong></div>
            ))}
          </div>
        </section>
        <section className="commercial-panel">
          <h3 className="commercial-section-title">Technician Load</h3>
          <DataTable columns={[{ key: 'displayName', label: 'Technician' }, { key: 'activeAssignedWorkOrders', label: 'Active Work Orders' }, { key: 'isDispatchable', label: 'Dispatch Ready', render: row => row.isDispatchable ? 'Yes' : 'No' }, { key: 'isActive', label: 'Active', render: row => row.isActive ? 'Yes' : 'No' }]} rows={reports?.technicianLoad || []} emptyMessage="No technician activity to show yet." />
        </section>
      </section>
    </>
  );
}
